import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, FileText, Trash2, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";

const ConsultationsPage = () => {
  const { token } = useAuth();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    patient_name: "", phone: "", date: new Date().toISOString().split('T')[0],
    consultation_type: "new", notes: "", status: "pending"
  });

  useEffect(() => {
    fetchConsultations();
  }, [token]);

  const fetchConsultations = async () => {
    try {
      const response = await api.get("/consultations", token);
      setConsultations(response.data);
    } catch (error) {
      // If endpoint doesn't exist yet, use empty array
      setConsultations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/consultations", formData, token);
      toast.success("تم إضافة الاستشارة بنجاح");
      setShowModal(false);
      setFormData({ patient_name: "", phone: "", date: new Date().toISOString().split('T')[0], consultation_type: "new", notes: "", status: "pending" });
      fetchConsultations();
    } catch (error) {
      toast.error("خطأ في إضافة الاستشارة");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/consultations/${id}`, { status }, token);
      toast.success("تم تحديث الحالة");
      fetchConsultations();
    } catch (error) {
      toast.error("خطأ في تحديث الحالة");
    }
  };

  const deleteConsultation = async (id) => {
    if (!window.confirm("هل أنت متأكد من الحذف؟")) return;
    try {
      await api.delete(`/consultations/${id}`, token);
      toast.success("تم الحذف");
      fetchConsultations();
    } catch (error) {
      toast.error("خطأ في الحذف");
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse-soft">جاري التحميل...</div></div>;

  const statusLabels = { pending: "قيد الانتظار", completed: "مكتملة", cancelled: "ملغية" };
  const statusColors = { pending: "bg-yellow-100 text-yellow-700", completed: "bg-green-100 text-green-700", cancelled: "bg-red-100 text-red-700" };

  return (
    <div className="animate-fade-in" data-testid="consultations-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="page-title">الاستشارات</h1>
          <p className="page-subtitle">إدارة استشارات ما قبل العملية</p>
        </div>
        <button onClick={() => setShowModal(true)} className="btn btn-primary">
          <Plus className="w-5 h-5" />
          إضافة استشارة
        </button>
      </div>

      {consultations.length === 0 ? (
        <div className="empty-state card p-8">
          <FileText className="empty-state-icon" />
          <h3>لا توجد استشارات</h3>
          <p>أضف استشارة جديدة للبدء</p>
        </div>
      ) : (
        <div className="space-y-4">
          {consultations.map((c) => (
            <div key={c.id} className="card p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-gray-800">{c.patient_name}</h3>
                  <p className="text-sm text-gray-500">{c.phone}</p>
                  <p className="text-sm text-gray-500 mt-1">{c.date}</p>
                  {c.notes && <p className="text-sm text-gray-600 mt-2">{c.notes}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs ${statusColors[c.status]}`}>
                    {statusLabels[c.status]}
                  </span>
                  <select 
                    className="form-input text-sm py-1 px-2"
                    value={c.status}
                    onChange={(e) => updateStatus(c.id, e.target.value)}
                  >
                    <option value="pending">قيد الانتظار</option>
                    <option value="completed">مكتملة</option>
                    <option value="cancelled">ملغية</option>
                  </select>
                  <button onClick={() => deleteConsultation(c.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">إضافة استشارة جديدة</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body space-y-4">
                <div className="form-group">
                  <label className="form-label">اسم المريض *</label>
                  <input type="text" className="form-input" value={formData.patient_name}
                    onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">رقم الهاتف *</label>
                  <input type="tel" className="form-input" value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">تاريخ الاستشارة *</label>
                  <input type="date" className="form-input" value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">نوع الاستشارة</label>
                  <select className="form-input" value={formData.consultation_type}
                    onChange={(e) => setFormData({ ...formData, consultation_type: e.target.value })}>
                    <option value="new">استشارة جديدة</option>
                    <option value="followup">متابعة</option>
                    <option value="pre_operation">قبل العملية</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">ملاحظات</label>
                  <textarea className="form-input" rows={3} value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn btn-primary">حفظ</button>
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">إلغاء</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultationsPage;
